import { useState, useEffect, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";

// Pages - LD Finance
import Landing from "./pages/Landing";
import Dashboard from "./pages/Dashboard";
import Transactions from "./pages/Transactions";
import Debts from "./pages/Debts";
import Savings from "./pages/Savings";
import Reports from "./pages/Reports";
import Survey from "./pages/Survey";
import Profile from "./pages/Profile";
import AuthCallback from "./pages/AuthCallback";
import Admin from "./pages/Admin";
import Community from "./pages/Community";
import Prestamos from "./pages/Prestamos";
import PendingApproval from "./pages/PendingApproval";
import DeudorApplicationForm from "./pages/DeudorApplicationForm";
import DeudorDashboard from "./pages/DeudorDashboard";
import InvestorDashboard from "./pages/InvestorDashboard";

// Pages - LD Holdings
import HoldingsHome from "./pages/HoldingsHome";
import HoldingsEmpresa from "./pages/HoldingsEmpresa";
import HoldingsServicios from "./pages/HoldingsServicios";
import HoldingsServiceDetail from "./pages/HoldingsServiceDetail";
import HoldingsContacto from "./pages/HoldingsContacto";
import { BlogListPage, BlogDetailPage } from "./pages/Blog";

// Components
import Layout from "./components/Layout";
import HoldingsLayout from "./components/HoldingsLayout";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Auth Context
import { createContext, useContext } from "react";

const AuthContext = createContext(null);
const ThemeContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within ThemeProvider");
  }
  return context;
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isImpersonating, setIsImpersonating] = useState(() => sessionStorage.getItem('is_impersonating') === 'true');
  const [impersonatedUser, setImpersonatedUser] = useState(() => {
    const stored = sessionStorage.getItem('impersonated_user');
    return stored ? JSON.parse(stored) : null;
  });

  const checkAuth = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        withCredentials: true,
      });
      setUser(response.data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      console.error("Logout error:", error);
    }
    setUser(null);
  };

  const startImpersonation = async (userId) => {
    try {
      const response = await axios.post(`${API}/admin/impersonate/${userId}`, {}, { withCredentials: true });
      const { user: targetUser } = response.data;
      // Backend already set the new httponly cookie — just save state
      sessionStorage.setItem('admin_user', JSON.stringify(user));
      sessionStorage.setItem('is_impersonating', 'true');
      sessionStorage.setItem('impersonated_user', JSON.stringify(targetUser));
      // Fetch the impersonated user's full data using the new cookie
      const meResponse = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setImpersonatedUser(targetUser);
      setIsImpersonating(true);
      setUser(meResponse.data);
      return true;
    } catch (error) {
      console.error("Impersonation error:", error);
      return false;
    }
  };

  const stopImpersonation = async () => {
    try {
      // Backend restores admin cookie and deletes impersonation session
      await axios.post(`${API}/admin/stop-impersonation`, {}, { withCredentials: true });
      sessionStorage.removeItem('admin_user');
      sessionStorage.removeItem('is_impersonating');
      sessionStorage.removeItem('impersonated_user');
      setIsImpersonating(false);
      setImpersonatedUser(null);
      // Re-fetch admin user from restored cookie
      await checkAuth();
    } catch (error) {
      console.error("Stop impersonation error:", error);
      sessionStorage.removeItem('admin_user');
      sessionStorage.removeItem('is_impersonating');
      sessionStorage.removeItem('impersonated_user');
      setIsImpersonating(false);
      setImpersonatedUser(null);
      await checkAuth();
    }
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, logout, checkAuth, isImpersonating, impersonatedUser, startImpersonation, stopImpersonation }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route
const ProtectedRoute = ({ children, requireRole }) => {
  const { user, loading, logout, isImpersonating } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!loading && !user) {
      navigate("/finance", { replace: true });
    }
  }, [user, loading, navigate]);

  // If user hasn't completed survey, redirect to survey (skip during impersonation, pending, deudor-only)
  useEffect(() => {
    if (user && !user.has_completed_survey && location.pathname !== "/survey" && !isImpersonating) {
      const roles = user.roles || [];
      const isDeudorOnly = roles.length === 1 && roles[0] === 'deudor';
      if (!isDeudorOnly && user.approval_status === 'approved') {
        navigate("/survey", { replace: true });
      }
    }
  }, [user, location.pathname, navigate, isImpersonating]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-muted-foreground">Cargando...</div>
      </div>
    );
  }

  if (!user) return null;

  // Check approval status (pending or denied)
  if (user.approval_status === 'pending' || user.approval_status === 'denied') {
    return <PendingApproval user={user} onLogout={logout} />;
  }

  // Check role requirement
  if (requireRole) {
    const roles = user.roles || [];
    if (!roles.includes(requireRole) && !user.is_admin) {
      navigate("/dashboard", { replace: true });
      return null;
    }
  }

  return children;
};

// Wrappers to pass user to role-specific pages
const DeudorDashboardWrapper = () => { const { user } = useAuth(); return <DeudorDashboard user={user} />; };
const InvestorDashboardWrapper = () => { const { user } = useAuth(); return <InvestorDashboard user={user} />; };
const DeudorApplicationFormWrapper = () => { const { user } = useAuth(); return <DeudorApplicationForm user={user} />; };


function AppRouter() {
  const location = useLocation();

  // Check URL fragment for session_id synchronously during render
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      {/* LD Holdings - Public Pages */}
      <Route path="/" element={<HoldingsLayout><HoldingsHome /></HoldingsLayout>} />
      <Route path="/nuestra-empresa" element={<HoldingsLayout><HoldingsEmpresa /></HoldingsLayout>} />
      <Route path="/servicios" element={<HoldingsLayout><HoldingsServicios /></HoldingsLayout>} />
      <Route path="/servicios/:slug" element={<HoldingsLayout><HoldingsServiceDetail /></HoldingsLayout>} />
      <Route path="/contacto" element={<HoldingsLayout><HoldingsContacto /></HoldingsLayout>} />
      <Route path="/blog" element={<HoldingsLayout><BlogListPage /></HoldingsLayout>} />
      <Route path="/blog/:slug" element={<HoldingsLayout><BlogDetailPage /></HoldingsLayout>} />

      {/* LD Finance - Auth */}
      <Route path="/finance" element={<Landing />} />
      <Route path="/auth/callback" element={<AuthCallback />} />

      {/* LD Finance - Protected Pages */}
      <Route
        path="/survey"
        element={
          <ProtectedRoute>
            <Survey />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Layout>
              <Dashboard />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/transactions"
        element={
          <ProtectedRoute>
            <Layout>
              <Transactions />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/debts"
        element={
          <ProtectedRoute>
            <Layout>
              <Debts />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/savings"
        element={
          <ProtectedRoute>
            <Layout>
              <Savings />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports"
        element={
          <ProtectedRoute>
            <Layout>
              <Reports />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <Layout>
              <Profile />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin"
        element={
          <ProtectedRoute>
            <Layout>
              <Admin />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/community"
        element={
          <ProtectedRoute>
            <Layout>
              <Community />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Préstamos - Admin Module */}
      <Route
        path="/prestamos/*"
        element={
          <ProtectedRoute>
            <Layout>
              <Prestamos />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Role-specific routes */}
      <Route
        path="/my-loans"
        element={
          <ProtectedRoute>
            <DeudorDashboardWrapper />
          </ProtectedRoute>
        }
      />
      <Route
        path="/my-portfolio"
        element={
          <ProtectedRoute>
            <InvestorDashboardWrapper />
          </ProtectedRoute>
        }
      />
      <Route
        path="/deudor-apply"
        element={
          <ProtectedRoute>
            <DeudorApplicationFormWrapper />
          </ProtectedRoute>
        }
      />
    </Routes>
  );
}

function App() {
  const [theme, setTheme] = useState(() => localStorage.getItem('ld-theme') || 'dark');

  const toggleTheme = () => {
    setTheme(prev => {
      const next = prev === 'dark' ? 'light' : 'dark';
      localStorage.setItem('ld-theme', next);
      return next;
    });
  };

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);

  return (
    <div className="App">
      <BrowserRouter>
        <ThemeContext.Provider value={{ theme, toggleTheme }}>
          <AuthProvider>
            <AppRouter />
          </AuthProvider>
        </ThemeContext.Provider>
      </BrowserRouter>
    </div>
  );
}

export default App;
